import { parseDocuments } from "@/pages/project/project-plan";
import { useMemo } from "react";
import { useMutation } from "@apollo/client/react";
import { useValidationFallback } from "@/components/match/use-match-editor";
import {
  LinkListingMatchDocument,
  SaveListingDocument,
  UnlinkListingMatchDocument,
} from "@/graphql/generated/operations";
import type { DashboardFacetKey } from "@/pages/project/dashboard-kinds";
import type { DashboardData, DashboardEntity, DashboardSource } from "@/pages/project/use-dashboard-source";
import { membersFromAccess } from "./listing-access";
import { throwIfRejected } from "./listing-form";
import { gallerySources } from "./listing-images";
import { useListing, type ListingDetail } from "./use-listing";

// Der Adapter des Projekt-Dashboards für die Art LISTING: gelesen über `listing(id)`, geschrieben über
// `updateListing` mit schmaler Antwort (SaveListing), gelöscht über `deleteListing`, Matches verknüpft
// und gelöst über `linkListingMatch` und `unlinkListingMatch`. Ob die Person das darf, sagt
// `actions.canManageMatches` (WRITE auf `candidates`), nicht `canUpdate`.

function listingEntity(listing: ListingDetail): DashboardEntity {
  const creator = listing.createdBy;
  const members = membersFromAccess(listing.access, creator?.id).map((member) => ({
    id: member.personId,
    name: member.formattedName,
  }));
  const filledFacets: DashboardFacetKey[] = [];
  if (parseDocuments(listing.data?.documents).length) filledFacets.push("dokumente");
  if (gallerySources(listing).length > 0) filledFacets.push("bilder");
  if (members.length > 0) filledFacets.push("mitglieder");
  // Verknüpfte Matches zeigen ihre Fläche auch dem, der sie nicht als Fläche speichern darf (nur WRITE
  // auf `candidates`, nicht auf `plan`).
  if ((listing.matches ?? []).length > 0) filledFacets.push("kandidaten");
  return {
    id: listing.id,
    name: listing.name ?? "",
    description: listing.description ?? "",
    data: (listing.data ?? {}) as DashboardData,
    createdAt: listing.createdAt ?? null,
    createdBy: creator
      ? {
          id: creator.id,
          name: creator.formattedName ?? "",
          avatarContent: creator.avatar?.content,
          avatarMime: creator.avatar?.mimeType,
        }
      : null,
    members,
    // Mitglieder und Leserschaft liest genesis nur Eingetragenen vor; für einen Gruppen-Empfänger ist
    // `access` null.
    isMember: listing.access != null,
    actions: {
      canChangeSearchTarget: listing.actions.canChangeSearchTarget,
      canUpdate: listing.actions.canUpdate,
      canManageAccess: listing.actions.canManageAccess,
      canDelete: listing.actions.canDelete,
      canManageMatches: listing.actions.canManageMatches,
      canReadApplications: listing.actions.canReadApplications,
    },
    matchIds: (listing.matches ?? []).map((match) => match.id),
    filledFacets,
  };
}

export function useListingSource(id: string): DashboardSource {
  const fallback = useValidationFallback();
  const { listing, loading, error, remove } = useListing(id);
  const [saveListing] = useMutation(SaveListingDocument);
  const [linkListingMatch] = useMutation(LinkListingMatchDocument);
  const [unlinkListingMatch] = useMutation(UnlinkListingMatchDocument);
  const entity = useMemo(() => (listing ? listingEntity(listing) : null), [listing]);
  return {
    loading,
    error,
    entity,
    save: async (patch) => {
      const result = await saveListing({ variables: { id, input: { data: patch, dryRun: false } } });
      throwIfRejected(result.data?.updateListing, fallback);
    },
    remove,
    // Die Antwort trägt `Listing.matches`; die Gegenrichtung `Match.listings` (Kopfzeile und Auto-Match des
    // Matches) steht in einem anderen Cache-Eintrag und wird beim nächsten Lesen neu geholt.
    linkMatch: async (matchId) => {
      await linkListingMatch({
        variables: { input: { listingId: id, matchId } },
        update: (cache) => {
          cache.evict({ id: cache.identify({ __typename: "Match", id: matchId }), fieldName: "listings" });
        },
      });
    },
    unlinkMatch: async (matchId) => {
      await unlinkListingMatch({
        variables: { input: { listingId: id, matchId } },
        update: (cache) => {
          cache.evict({ id: cache.identify({ __typename: "Match", id: matchId }), fieldName: "listings" });
        },
      });
    },
  };
}
